import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Csel,
  Destructive,
  Icon,
  Input,
  Modal,
  Section,
  Switch,
  useToast,
} from '@efira/ui';

import {
  useAddBinding,
  useDeleteBinding,
  useRestoreBinding,
  useRunBinding,
  useSaveBinding,
} from '../../../api/queries';
import type {
  BindableAction,
  Binding,
  BindingsPayload,
  GiftsPayload,
  TriggerCondition,
} from '../../../api/queries';
import { EVENT_LABELS, coins, label, valueLabel } from '../../../bindings/labels';
import { GiftPicker } from '../../../bindings/GiftPicker';
import { Presets } from './Presets';
import { TriggerHud } from './TriggerHud';

/**
 * «Привязки действий» в левой колонке консоли.
 *
 * ФОРМА ЭТАЛОНА, МОДЕЛЬ НАША. У эталона строка читается как «КОГДА ‹событие›
 * ‹уточнение› ‹порог› ТОГДА ‹действие› ×N», группы собраны по действию, у
 * группы свой выключатель, а в заголовке секции две кнопки-иконки: показ
 * легенды на оверлее и широкий редактор (docs/reference/LIVECADE_UX.md, 4.1).
 * Всё это здесь есть.
 *
 * Наша модель богаче и не теряется: условия соединяются по ИЛИ, есть условие
 * И, кулдаун, приоритет и множитель (`docs/ARCHITECTURE.md`, 4.2). Базовый
 * случай остаётся в три поля, остальное раскрывается по требованию - иначе
 * повторилась бы ошибка эталона с другого конца: восемь полей ради «роза
 * запускает толчок».
 *
 * ПРАВИЛА ВИДИМОСТИ СОХРАНЕНЫ: кнопка добавления при исчерпанном пределе
 * видна и неактивна, предел назван числом, причина недоступности - словами.
 */
const KEYWORD_KEYS = [
  'trigger.keyword.play',
  'trigger.keyword.red',
  'trigger.keyword.blue',
  'trigger.keyword.yes',
  'trigger.keyword.no',
  'ticker.keyword.go',
];

const EVENTS = ['gift', 'comment', 'like', 'follow', 'share', 'join'] as const;

function defaultCondition(event: (typeof EVENTS)[number]): TriggerCondition {
  switch (event) {
    case 'gift':
      return { event: 'gift', anyGift: true, minCoins: 1 };
    case 'comment':
      return { event: 'comment', keywordKey: KEYWORD_KEYS[0] as string };
    case 'like':
      return { event: 'like', likeThreshold: 50 };
    default:
      return { event } as TriggerCondition;
  }
}

export function Triggers({
  gameId,
  data,
  gifts,
}: {
  gameId: string;
  data: BindingsPayload;
  gifts: GiftsPayload | undefined;
}) {
  const [hud, setHud] = useState(false);
  const [wide, setWide] = useState(false);
  const [presets, setPresets] = useState(false);

  // Секции нет у игр без привязываемых действий - как у эталона: у «Угадай
  // число» её тоже нет, и пустая секция там не рисуется.
  if (data.manifest.actions.length === 0) {
    return null;
  }

  const list = (
    <TriggerList
      gameId={gameId}
      data={data}
      gifts={gifts}
      onOpenHud={() => {
        setHud(true);
      }}
    />
  );

  return (
    <>
      <Section
        title="Привязки действий"
        id="action-triggers"
        actions={
          <>
            {/* Вход в готовые наборы - ссылкой в шапке секции: набор меняет
                все привязки разом, и место ему рядом с ними, а не на
                отдельном экране, куда надо уходить из консоли. */}
            {data.manifest.presets.length > 0 && (
              <Button
                variant="ghost"
                size="s"
                icon={<Icon name="widgets" size="sm" />}
                className="@max-[260px]:hidden"
                data-at="presets-btn"
                onClick={() => {
                  setPresets(true);
                }}
              >
                Наборы
              </Button>
            )}
            <Button
              variant="ghost"
              size="s"
              className="px-1"
              data-at="hud-btn"
              aria-pressed={hud}
              aria-label="Показ легенды на оверлее"
              title="Показ легенды на оверлее"
              onClick={() => {
                setHud((value) => !value);
              }}
            >
              <Icon name="layout" size="sm" />
            </Button>
            <Button
              variant="ghost"
              size="s"
              className="px-1"
              data-at="expand-btn"
              aria-label="Открыть привязки в широком редакторе"
              title="Широкий редактор"
              onClick={() => {
                setWide(true);
              }}
            >
              <Icon name="open" size="sm" />
            </Button>
          </>
        }
      >
        {hud ? (
          <TriggerHud
            gameId={gameId}
            onBack={() => {
              setHud(false);
            }}
          />
        ) : (
          list
        )}
      </Section>

      {/*
        Широкий редактор - то же содержимое, больше места. Отдельной правды о
        привязках в нём нет: две реализации одного списка разошлись бы, и
        стример увидел бы разное в колонке и в окне.

        Окно МОНТИРУЕТСЯ ТОЛЬКО ОТКРЫТЫМ. Закрытое, оно всё равно держало бы в
        разметке второй экземпляр каждой группы и свою подложку - подложка
        перехватывала нажатия по колонке, и панель переставала работать.
      */}
      <Presets
        gameId={gameId}
        presets={data.manifest.presets}
        actions={data.manifest.actions}
        current={data.bindings}
        gifts={gifts}
        open={presets}
        onClose={() => {
          setPresets(false);
        }}
      />

      {wide && (
        <Modal
          wide
          open={wide}
          onClose={() => {
            setWide(false);
          }}
          title="Привязки действий"
          actions={
            <Button
              variant="secondary"
              onClick={() => {
                setWide(false);
              }}
            >
              Закрыть
            </Button>
          }
        >
          {/* Содержимое ТЯНЕТСЯ по панели, а не задаёт себе ширину: панель
              решает, сколько места есть. */}
          <div data-at="wide" className="w-full min-w-0">
            <TriggerList gameId={gameId} data={data} gifts={gifts} onOpenHud={null} />
          </div>
        </Modal>
      )}
    </>
  );
}

function TriggerList({
  gameId,
  data,
  gifts,
  onOpenHud,
}: {
  gameId: string;
  data: BindingsPayload;
  gifts: GiftsPayload | undefined;
  onOpenHud: (() => void) | null;
}) {
  const byAction = useMemo(() => {
    const map = new Map<string, Binding[]>();

    for (const binding of data.bindings) {
      map.set(binding.actionId, [...(map.get(binding.actionId) ?? []), binding]);
    }

    return map;
  }, [data.bindings]);

  return (
    <div className="flex flex-col gap-2">
      {onOpenHud !== null && (
        <p className="text-foreground/80 text-sm">
          Событие зрителя плюс уточнение и порог запускают действие игры.
        </p>
      )}

      {data.manifest.actions.map((action) => (
        <TriggerGroup
          key={action.id}
          gameId={gameId}
          action={action}
          bindings={byAction.get(action.id) ?? []}
          gifts={gifts}
        />
      ))}
    </div>
  );
}

/**
 * Группа привязок одного действия.
 *
 * Выключатель группы гасит ВСЕ её привязки разом - у эталона «Disable ‹имя
 * группы›». Наша реализация честнее: выключенная привязка остаётся на месте и
 * возвращается тем же тумблером, а не удаляется.
 */
function TriggerGroup({
  gameId,
  action,
  bindings,
  gifts,
}: {
  gameId: string;
  action: BindableAction;
  bindings: Binding[];
  gifts: GiftsPayload | undefined;
}) {
  const save = useSaveBinding(gameId);
  const add = useAddBinding(gameId);
  const remove = useDeleteBinding(gameId);
  const toast = useToast();

  const enabled = bindings.some((binding) => binding.enabled !== false);
  const limit = action.maxConditions;
  const atLimit = limit !== undefined && bindings.length >= limit;

  /**
   * Подгруппы по значению параметра привязки.
   *
   * У эталона это «Small push» внутри «Team G push (gift)»: одно действие с
   * разной силой. Значение параметра и есть подгруппа - отдельной сущности в
   * модели для этого не нужно.
   */
  const param = action.params.find((item) => item.scope === 'binding' && item.type === 'enum');

  const subgroups = useMemo(() => {
    if (param === undefined) {
      return [{ value: null as string | null, bindings }];
    }

    const map = new Map<string | null, Binding[]>();

    for (const binding of bindings) {
      const value = binding.params?.[param.id];
      const key = value === undefined ? null : String(value);
      map.set(key, [...(map.get(key) ?? []), binding]);
    }

    return [...map.entries()].map(([value, items]) => ({ value, bindings: items }));
  }, [bindings, param]);

  const addBinding = () => {
    add.mutate(
      {
        actionId: action.id,
        ...(param === undefined || subgroups[0]?.value === null || subgroups[0]?.value === undefined
          ? {}
          : { params: { [param.id]: subgroups[0].value } }),
      },
      {
        onSuccess: () => {
          toast.show({ tone: 'success', title: 'Привязка добавлена' });
        },
        onError: (error) => {
          const reason =
            typeof error === 'object' && error !== null && 'reason' in error
              ? String((error as { reason: unknown }).reason)
              : 'Привязка не добавилась. Попробуйте ещё раз.';
          toast.show({ tone: 'warning', title: 'Не добавилось', text: reason });
        },
      },
    );
  };

  return (
    /* Без карточной обёртки: у эталона колонка плоская, а у нас было три
       уровня рамок подряд - секция, группа, строка - и содержимое теряло
       12 px ширины на каждом. Группы разделяет линия. */
    <section
      data-at-group={action.id}
      className="border-foreground/10 border-t pt-2 first:border-0 first:pt-0"
    >
      {/* `pe-3` - не украшение, а место под ОБЛАСТЬ НАЖАТИЯ переключателя:
          у контролов пресета она на 12 px шире самого контрола (палец), и
          без отступа она вылезает за ряд. Ворота строк это увидели: «386 px
          шире строки 375 px». */}
      <header
        data-control-row="at-group-head"
        className="flex items-center gap-2 overflow-hidden pe-3"
      >
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {label(action.labelKey)}
        </span>

        <Switch
          data-at-group-toggle={action.id}
          aria-label={`Выключить «${label(action.labelKey)}»`}
          checked={enabled}
          disabled={bindings.length === 0}
          title={bindings.length === 0 ? 'У действия нет привязок' : undefined}
          onCheckedChange={(next) => {
            for (const binding of bindings) {
              save.mutate({ ...binding, enabled: next === true });
            }
          }}
        />
      </header>

      <div className="flex flex-col gap-2 pt-2">
        {bindings.length === 0 && (
          <p className="text-foreground/80 text-sm">
            Действие сейчас ничем не запускается. Добавьте привязку.
          </p>
        )}

        {subgroups.map((group) => (
          <div key={group.value ?? 'none'} className="flex flex-col gap-2">
            {group.value !== null && param !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-foreground/80 min-w-0 flex-1 truncate text-xs">
                  {label(`${param.labelKey}.${group.value}`) === `${param.labelKey}.${group.value}`
                    ? valueLabel(group.value)
                    : label(`${param.labelKey}.${group.value}`)}
                </span>
                {/* Удаление подгруппы: у эталона «Remove Small push», и
                    заводское значение после него не возвращается ничем. У нас
                    возвращается - сбросом привязок к заводским. */}
                <Destructive
                  id="trigger-group-delete"
                  className="px-1"
                  data={{ 'data-at-param-remove': group.value }}
                  ariaLabel={`Удалить подгруппу «${valueLabel(group.value)}»`}
                  icon={<Icon name="delete" size="sm" />}
                  title={`Удалить подгруппу «${valueLabel(group.value)}»?`}
                  confirm="Удалить"
                  onConfirm={() => {
                    for (const binding of group.bindings) {
                      remove.mutate(binding.key);
                    }
                    toast.show({ tone: 'success', title: 'Подгруппа удалена' });
                  }}
                >
                  Уйдут все привязки подгруппы. Заводские вернёт сброс настроек игры.
                </Destructive>
              </div>
            )}

            {group.bindings.map((binding) => (
              <TriggerRow key={binding.key} gameId={gameId} binding={binding} gifts={gifts} />
            ))}
          </div>
        ))}

        {/* Правила 2 и 3: кнопка видна ВСЕГДА, при исчерпанном пределе
            неактивна и объясняет причину, предел назван числом рядом. */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="s"
            className="gap-1"
            data-at-add={action.id}
            disabled={atLimit}
            title={atLimit ? `Предел — ${limit} привязок на действие` : undefined}
            onClick={addBinding}
          >
            <Icon name="add" size="sm" />
            Добавить привязку
          </Button>
          <span className="text-foreground/80 text-xs">
            {limit === undefined
              ? `привязок: ${bindings.length}`
              : `${bindings.length} из ${limit}`}
          </span>
        </div>
      </div>
    </section>
  );
}

/** Одна привязка: «когда ‹событие› ‹уточнение› ‹порог› — тогда действие». */
function TriggerRow({
  gameId,
  binding,
  gifts,
}: {
  gameId: string;
  binding: Binding;
  gifts: GiftsPayload | undefined;
}) {
  const save = useSaveBinding(gameId);
  const remove = useDeleteBinding(gameId);
  const restore = useRestoreBinding(gameId);
  const run = useRunBinding();
  const toast = useToast();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [more, setMore] = useState(false);

  const condition = binding.conditions[0] ?? defaultCondition('gift');
  const exactGift = condition.event === 'gift' && 'giftId' in condition;
  const gift = exactGift
    ? gifts?.gifts.find((item) => item.id === (condition as { giftId: string }).giftId)
    : undefined;

  const patch = (next: Partial<Binding>) => {
    save.mutate({ ...binding, ...next });
  };

  const setCondition = (next: TriggerCondition) => {
    patch({ conditions: [next, ...binding.conditions.slice(1)] });
  };

  return (
    <article
      data-at-row={binding.key}
      className={`bg-muted/40 flex flex-col gap-2 rounded p-2 ${
        binding.enabled === false ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-foreground/80 shrink-0 text-xs tracking-wide uppercase">Когда</span>

        <span data-at-when={binding.key} className="min-w-0 flex-1">
          <Csel
            value={condition.event}
            aria-label="Событие зрителя"
            triggerClassName="w-full"
            options={EVENTS.map((event) => ({
              value: event,
              label: EVENT_LABELS[event] ?? event,
            }))}
            onChange={(next) => {
              setCondition(defaultCondition(next as (typeof EVENTS)[number]));
            }}
          />
        </span>
      </div>

      {condition.event === 'gift' && (
        <>
          <span data-at-gift-kind={binding.key}>
            <Csel
              value={exactGift ? 'exact' : 'any'}
              aria-label="Какой подарок"
              triggerClassName="w-full"
              options={[
                { value: 'exact', label: 'Конкретный подарок' },
                { value: 'any', label: 'Любой подарок от порога' },
              ]}
              onChange={(next) => {
                setCondition(
                  next === 'exact'
                    ? { event: 'gift', giftId: gifts?.gifts[0]?.id ?? 'rose' }
                    : { event: 'gift', anyGift: true, minCoins: 1 },
                );
              }}
            />
          </span>

          {exactGift ? (
            <>
              <Button
                variant="secondary"
                size="s"
                className="justify-start gap-2"
                data-at-gift={binding.key}
                aria-expanded={pickerOpen}
                onClick={() => {
                  setPickerOpen((value) => !value);
                }}
              >
                <Icon name="search" size="sm" />
                <span className="min-w-0 truncate">
                  {gift === undefined
                    ? (condition as { giftId: string }).giftId
                    : `${gift.name} · ${coins(gift.coins)}`}
                </span>
              </Button>

              {pickerOpen && gifts !== undefined && (
                <GiftPicker
                  gifts={gifts.gifts}
                  value={(condition as { giftId: string }).giftId}
                  onSelect={(giftId) => {
                    setCondition({ event: 'gift', giftId });
                    setPickerOpen(false);
                  }}
                />
              )}
            </>
          ) : (
            <label className="flex items-center gap-2 text-xs">
              <span className="text-foreground/80 shrink-0">От, монет</span>
              <Input
                type="number"
                min={1}
                data-at-count={binding.key}
                value={condition.minCoins}
                onChange={(event) => {
                  setCondition({
                    event: 'gift',
                    anyGift: true,
                    minCoins: Math.max(1, Number(event.target.value) || 1),
                  });
                }}
                className="w-24"
              />
            </label>
          )}
        </>
      )}

      {condition.event === 'comment' && (
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-foreground/80">Ключевое слово</span>
          <span data-at-keyword={binding.key}>
            <Csel
              value={condition.keywordKey}
              aria-label="Ключевое слово"
              triggerClassName="w-full"
              options={KEYWORD_KEYS.map((key) => ({
                value: key,
                label: label(key) === key ? (key.split('.').at(-1) ?? key) : label(key),
              }))}
              onChange={(next) => {
                setCondition({ event: 'comment', keywordKey: next });
              }}
            />
          </span>
          {/* Слово берётся из языкового пакета: оно меняется вместе с языком
              эфира, и вписанное руками «го» перестало бы работать на другом. */}
          <span className="text-foreground/80">Слово меняется вместе с языком эфира</span>
        </label>
      )}

      {condition.event === 'like' && (
        <label className="flex items-center gap-2 text-xs">
          <span className="text-foreground/80 shrink-0">Каждые, лайков</span>
          <Input
            type="number"
            min={1}
            data-at-count={binding.key}
            value={condition.likeThreshold}
            onChange={(event) => {
              setCondition({
                event: 'like',
                likeThreshold: Math.max(1, Number(event.target.value) || 1),
              });
            }}
            className="w-24"
          />
        </label>
      )}

      <div data-control-row="at-row-actions" className="flex items-center gap-1 overflow-hidden">
        <Switch
          data-at-enabled={binding.key}
          aria-label="Привязка включена"
          checked={binding.enabled !== false}
          onCheckedChange={(next) => {
            patch({ enabled: next === true });
          }}
          className="me-1"
        />

        {/* «Прогнать» ужимается первой: кнопки пресета шире прежних
            `btn-xs`, и в узкой колонке ряд действий привязки перестал
            помещаться (ворота строк: «содержимое 212 px шире строки
            199 px»). Подпись сокращается многоточием, значки остаются. */}
        <Button
          variant="ghost"
          size="s"
          icon={<Icon name="play" size="sm" />}
          className="min-w-0 shrink truncate"
          data-at-test-btn={binding.key}
          onClick={() => {
            run.mutate(sampleEvent(condition, gifts), {
              onSuccess: (result) => {
                toast.show({
                  tone: result.fired > 0 ? 'success' : 'warning',
                  title:
                    result.fired > 0
                      ? `Сработало: команд ${result.fired}`
                      : 'Не сработало: эфир не запущен или условие не совпало',
                });
              },
            });
          }}
        >
          Прогнать
        </Button>

        <Button
          variant="ghost"
          size="s"
          className="px-1 @max-[260px]:hidden"
          data-at-more={binding.key}
          aria-expanded={more}
          aria-label="Множитель, кулдаун, приоритет"
          title="Множитель, кулдаун, приоритет"
          onClick={() => {
            setMore((value) => !value);
          }}
        >
          <Icon name="settings" size="sm" />
        </Button>

        <Button
          variant="ghost"
          size="s"
          className="ms-auto px-1 @max-[300px]:hidden"
          data-at-restore={binding.key}
          aria-label="Вернуть заводскую привязку"
          title="Вернуть заводскую"
          onClick={() => {
            restore.mutate(binding.key, {
              onSuccess: () => {
                toast.show({ tone: 'success', title: 'Вернули заводскую привязку' });
              },
            });
          }}
        >
          <Icon name="arrowReset" size="sm" />
        </Button>

        <Destructive
          id="trigger-delete"
          className="px-1"
          data={{ 'data-at-remove-btn': binding.key }}
          ariaLabel="Удалить привязку"
          icon={<Icon name="delete" size="sm" />}
          title="Удалить привязку?"
          confirm="Удалить"
          onConfirm={() => {
            remove.mutate(binding.key);
          }}
        >
          Событие перестанет запускать это действие в игре. Вернуть заводские привязки можно сбросом
          настроек игры.
        </Destructive>
      </div>

      {more && (
        <div data-at-extra={binding.key} className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-xs">
            Множитель
            <Input
              type="number"
              min={1}
              value={binding.multiplier ?? 1}
              onChange={(event) => {
                patch({ multiplier: Math.max(1, Number(event.target.value) || 1) });
              }}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs">
            Кулдаун, с
            <Input
              type="number"
              min={0}
              value={Math.round((binding.cooldownMs ?? 0) / 1000)}
              onChange={(event) => {
                patch({ cooldownMs: Math.max(0, Number(event.target.value) || 0) * 1000 });
              }}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs">
            Приоритет
            <Input
              type="number"
              value={binding.priority ?? 0}
              onChange={(event) => {
                patch({ priority: Number(event.target.value) || 0 });
              }}
            />
          </label>

          <label className="col-span-2 flex items-center gap-2 text-xs">
            <Checkbox
              checked={(binding.requires ?? []).some((item) => item.kind === 'follower')}
              onCheckedChange={(next) => {
                patch({
                  requires:
                    next === true
                      ? [...(binding.requires ?? []), { kind: 'follower' }]
                      : (binding.requires ?? []).filter((item) => item.kind !== 'follower'),
                });
              }}
            />
            И зритель подписан на канал
          </label>
        </div>
      )}
    </article>
  );
}

/** Событие для прогона: то, что настроено, а не абстрактный подарок. */
function sampleEvent(condition: TriggerCondition, gifts: GiftsPayload | undefined): unknown {
  const base = {
    id: `run-${Date.now()}`,
    origin: 'synthetic' as const,
    at: Date.now(),
    actor: { id: 'dashboard-run' },
  };

  if (condition.event === 'gift') {
    if ('giftId' in condition) {
      const gift = gifts?.gifts.find((item) => item.id === condition.giftId);

      return {
        ...base,
        type: 'gift',
        giftId: gift?.vendorId ?? condition.giftId,
        coins: gift?.coins ?? 1,
        count: 1,
        streakFinished: true,
      };
    }

    return {
      ...base,
      type: 'gift',
      giftId: 'run',
      coins: condition.minCoins,
      count: 1,
      streakFinished: true,
    };
  }

  if (condition.event === 'comment') {
    return { ...base, type: 'comment', text: 'го' };
  }

  if (condition.event === 'like') {
    return { ...base, type: 'like', count: condition.likeThreshold };
  }

  return { ...base, type: condition.event };
}
